import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import dayjs from 'dayjs';

import { DateRangePickerService } from '../components/date-range-picker/date-range-picker.service';
import { convertToFilterCondition } from '../shared/appUtil';
import {
  buildDrilldownFilters,
  buildNovaDraftPrompt,
  capExplicitDateRange,
  ChartQueryContext,
  contextCanDrilldown,
  DrilldownOpenOptions,
  mergeChartQueryContext,
  readChartQueryContext,
  ResolvedChartDateRange,
  resolveEffectiveDateRange,
  toInsightColumnName,
  unwrapChartRequest,
} from '../shared/chart-insights-drilldown.util';
import type { FilterRequest } from '../shared/types';
import { ChartData, UserDashboardService } from './user-dashboard.service';
import { FilterService } from './filter.service';
import { NotificationService } from './notification.service';
import { NovaServiceService } from './nova-service.service';
import { SharedDataService } from './shared-data.service';

/**
 * Opens the Insights data view with the chart's filters and date range,
 * plus the table value (or values) the user picked.
 */
@Injectable({
  providedIn: 'root',
})
export class ChartInsightsDrilldownService {
  /** Tells Insights to land on call records instead of resetting to chart view. */
  private pendingDataView = false;

  constructor(
    private router: Router,
    private userDashboardService: UserDashboardService,
    private filterService: FilterService,
    private sharedDataService: SharedDataService,
    private dateRangePickerService: DateRangePickerService,
    private notificationService: NotificationService,
    private novaService: NovaServiceService
  ) {}

  consumePendingDataView(): boolean {
    const pending = this.pendingDataView;
    this.pendingDataView = false;
    return pending;
  }

  /** Chart setup used by the confirmation and by the Insights navigation. */
  loadContext(chart: ChartData | null): Observable<ChartQueryContext> {
    const local = readChartQueryContext(chart);
    if (!chart?.id) {
      return of(local);
    }
    return this.userDashboardService.getChartForEditing(chart.id).pipe(
      map((response) => mergeChartQueryContext(local, readChartQueryContext(unwrapChartRequest(response)))),
      catchError(() => of(local))
    );
  }

  open(chart: ChartData | null, values: string[], options: DrilldownOpenOptions = {}): Observable<void> {
    return this.withChartContext(chart, values, options, 'Insights', (context, names) =>
      this.navigate(context, names, options)
    );
  }

  /** Same row filters and dates as Insights, opened as a new NOVA conversation. */
  openInNova(chart: ChartData | null, values: string[], options: DrilldownOpenOptions = {}): Observable<void> {
    return this.withChartContext(chart, values, options, 'NOVA', (context, names) =>
      this.navigateToNova(context, names, options)
    );
  }

  private withChartContext(
    chart: ChartData | null,
    values: string[],
    options: DrilldownOpenOptions,
    target: 'Insights' | 'NOVA',
    next: (context: ChartQueryContext, names: string[]) => Observable<void>
  ): Observable<void> {
    if (!chart) {
      this.notificationService.error(`${target} unavailable`, 'Chart data is not loaded.');
      return throwError(() => new Error('missing chart'));
    }

    const names = uniqueValues(values);
    const periodOnly = Boolean(
      options.skipDimensionFilter && options.dateRange?.startDate && options.dateRange?.endDate
    );
    if (!names.length && !periodOnly) {
      this.notificationService.error(`${target} unavailable`, 'This row has no value to filter on.');
      return throwError(() => new Error('missing value'));
    }

    const local = readChartQueryContext(chart);
    const proceed = (context: ChartQueryContext) => (
      this.canOpen(context, options) ? next(context, names) : this.incomplete(target)
    );

    // Saved charts keep dimension, filters, and dates on the edit API. Load that
    // before opening, and fall back to the chart already on screen.
    if (!chart.id) {
      return proceed(local);
    }

    return this.userDashboardService.getChartForEditing(chart.id).pipe(
      switchMap((response) => proceed(
        mergeChartQueryContext(local, readChartQueryContext(unwrapChartRequest(response)))
      )),
      catchError((error) => {
        if (error?.message === 'incomplete chart') {
          return throwError(() => error);
        }
        return proceed(local);
      })
    );
  }

  private canOpen(context: ChartQueryContext, options: DrilldownOpenOptions): boolean {
    if (options.dateRange?.startDate && options.dateRange?.endDate) {
      return Boolean(options.skipDimensionFilter) || Boolean(context.dimension);
    }
    return contextCanDrilldown(context);
  }

  private incomplete(target: 'Insights' | 'NOVA'): Observable<void> {
    const detail = target === 'NOVA'
      ? 'This chart is missing its dimension or date range, so NOVA cannot be filtered.'
      : 'This chart is missing its dimension or date range, so Insights cannot be filtered.';
    this.notificationService.error(`${target} unavailable`, detail);
    return throwError(() => new Error('incomplete chart'));
  }

  private prepareFilters(
    context: ChartQueryContext,
    values: string[],
    options: DrilldownOpenOptions
  ): { range: ResolvedChartDateRange; filters: FilterRequest[] } | null {
    const range = options.dateRange?.startDate && options.dateRange?.endDate
      ? capExplicitDateRange(options.dateRange.startDate, options.dateRange.endDate)
      : resolveEffectiveDateRange(context);
    if (!range || (!options.skipDimensionFilter && !context.dimension)) {
      return null;
    }

    const secondaryColumn = context.secondaryDimension;
    const secondary = options.secondaryValue && secondaryColumn
      ? { column: secondaryColumn, value: options.secondaryValue }
      : null;
    const filters = options.skipDimensionFilter
      ? appendSecondaryFilter(context.filters, secondary)
      : buildDrilldownFilters(context.filters, context.dimension as string, values, secondary);
    return { range, filters };
  }

  private navigate(context: ChartQueryContext, values: string[], options: DrilldownOpenOptions): Observable<void> {
    const prepared = this.prepareFilters(context, values, options);
    if (!prepared) {
      return this.incomplete('Insights');
    }

    const { range, filters } = prepared;
    const conditions = convertToFilterCondition(filters);
    this.filterService.updateFilterData(conditions);
    this.sharedDataService.updateDashboardState('filters', conditions);
    this.sharedDataService.setSearchTerm('');

    if (context.timeZone && context.timeZone !== this.sharedDataService.selectedTimezoneValue) {
      this.sharedDataService.setSelectedTimezoneValue(context.timeZone);
    }

    const start = dayjs(range.startDate);
    const end = dayjs(range.endDate);
    const dateRange = {
      startDate: start,
      endDate: end,
      label: `${start.format('DD MMM YYYY')} - ${end.format('DD MMM YYYY')}`,
    };
    this.dateRangePickerService.setSelectedRangeCalendar(dateRange);
    this.sharedDataService.setDateRangeObject(dateRange);
    this.sharedDataService.updateDashboardState('dateRange', dateRange);
    this.sharedDataService.setDateRange({
      startAPIDate: range.startDate,
      endAPIDate: range.endDate,
      dateSelected: true,
    });

    this.sharedDataService.updateDashboardState('tableViewFlag', true);
    this.sharedDataService.setTableViewFlagStatus(true);
    this.sharedDataService.updateDashboardState('navBarData', {
      routeLink: 'conversation-insights',
      icon: 'assets/icons/bar_chart_icon.svg',
      label: 'Insights Dashboard',
    });

    this.pendingDataView = true;
    const column = context.dimension ? toInsightColumnName(context.dimension) : '';
    const valueLabel = values.length === 1 ? values[0] : `${values.length} values`;
    const from = start.format('DD MMM YYYY');
    const to = end.format('DD MMM YYYY');
    let message = options.skipDimensionFilter
      ? `Showing Insights for ${from} – ${to}.`
      : `Showing Insights for ${column}: ${valueLabel}. Date range ${from} – ${to}.`;
    if (options.secondarySkipped) {
      message += ' Only the main breakdown is included.';
    }
    if (range.capped) {
      message += ' Limited to 92 days.';
    }
    this.notificationService.info('Insights', message, 6000);
    this.router.navigate(['/conversation-insights']);
    return of(undefined);
  }

  private navigateToNova(
    context: ChartQueryContext,
    values: string[],
    options: DrilldownOpenOptions
  ): Observable<void> {
    const prepared = this.prepareFilters(context, values, options);
    if (!prepared) {
      return this.incomplete('NOVA');
    }

    const { range, filters } = prepared;
    const start = dayjs(range.startDate);
    const end = dayjs(range.endDate);
    this.novaService.beginChartHandoff(
      {
        start_date: range.startDate,
        end_date: range.endDate,
        timeZone: context.timeZone || 'EST',
        filters,
      },
      buildNovaDraftPrompt()
    );

    const column = context.dimension ? toInsightColumnName(context.dimension) : '';
    const valueLabel = values.length === 1 ? values[0] : `${values.length} values`;
    const from = start.format('DD MMM YYYY');
    const to = end.format('DD MMM YYYY');
    let message = options.skipDimensionFilter
      ? `NOVA is filtered to ${from} – ${to}.`
      : `NOVA is filtered to ${column}: ${valueLabel}. Date range ${from} – ${to}.`;
    if (options.secondarySkipped) {
      message += ' Only the main breakdown is included.';
    }
    if (range.capped) {
      message += ' Limited to 92 days.';
    }
    this.notificationService.info('NOVA', message, 6000);
    this.router.navigate(['/nova']);
    return of(undefined);
  }
}

function appendSecondaryFilter(
  existing: FilterRequest[],
  secondary: { column: string; value: string } | null
): FilterRequest[] {
  const filters = (existing || []).map((filter) => ({
    ...filter,
    logicalOperator: filter.logicalOperator || 'AND',
    isClusterFilter: Boolean(filter.isClusterFilter),
    clusterFilters: Array.isArray(filter.clusterFilters)
      ? filter.clusterFilters.map((cluster) => ({ ...cluster }))
      : [],
  }));
  const column = secondary?.column?.trim();
  const value = secondary?.value?.trim();
  if (column && value) {
    filters.push({
      logicalOperator: 'AND',
      columnName: toInsightColumnName(column),
      operationType: 'is',
      value,
      isClusterFilter: false,
      clusterFilters: [],
    });
  }
  return filters;
}

function uniqueValues(values: string[]): string[] {
  const seen = new Set<string>();
  const result: string[] = [];
  values.forEach((value) => {
    const text = String(value ?? '').trim();
    const key = text.toLowerCase();
    if (!text || seen.has(key)) {
      return;
    }
    seen.add(key);
    result.push(text);
  });
  return result;
}
